/**
 * confirmDialogStore — global state for the app-wide ConfirmDialog
 * (Radix AlertDialog). Callers dispatch via `openConfirmDialog(...)` and the
 * single `<GlobalConfirmDialog />` mounted at the app root renders it.
 *
 * Why global instead of inline rendering? Radix AlertDialog stacked inside an
 * open Radix Dialog (e.g. the Settings dialog or any other modal hosting a
 * confirm prompt) leaves `body { pointer-events: none }` stuck after the
 * inner alert closes — two overlay layers race their pointer-events locks
 * and the outer Dialog's "still open" gate keeps the global cleanup
 * observer from clearing it. Mounting the AlertDialog at the app root, as a
 * sibling of every Dialog, avoids the stacking conflict entirely.
 */

import { create } from 'zustand';
import type { ReactNode } from 'react';

export type ConfirmDialogVariant = 'destructive' | 'default';

export type ConfirmDialogRequest = {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  requireTypedName?: { value: string; label: string };
  onConfirm: () => void | Promise<void>;
  onError?: (err: unknown) => void;
};

type ConfirmDialogState = {
  isOpen: boolean;
  request: ConfirmDialogRequest | null;
};

type ConfirmDialogActions = {
  openConfirm: (req: ConfirmDialogRequest) => void;
  closeConfirm: () => void;
};

type ConfirmDialogStore = ConfirmDialogState & ConfirmDialogActions;

export const useConfirmDialogStore = create<ConfirmDialogStore>((set) => ({
  isOpen: false,
  request: null,
  openConfirm: (req) => {
    // Defer one frame so that any in-flight Radix overlay close (e.g. the
    // ContextMenu that triggered the action, or the dropdown that hosted
    // the delete button) finishes BEFORE this AlertDialog mounts. Mounting
    // a Radix overlay mid-close on another overlay leaves
    // `body { pointer-events: none }` stuck because the two overlay layers
    // race their pointer-events locks. Mirrors the rationale in
    // `declareJsonColumnDialogStore.ts`.
    const open = () => set({ isOpen: true, request: req });
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(open);
    } else {
      setTimeout(open, 0);
    }
  },
  closeConfirm: () => set({ isOpen: false }),
}));

export const openConfirmDialog = (req: ConfirmDialogRequest) =>
  useConfirmDialogStore.getState().openConfirm(req);
